/**
 * Turn an uploaded file into sheets of string cells, whatever its format.
 *
 * Supported: .csv / .tsv / .txt (text) and .xlsx (Excel 2007+, Google
 * Sheets "Download as .xlsx"). Not supported: the old binary .xls, .numbers
 * and .ods; those get a message saying how to re-save the file.
 */
import { parseCsv } from './csv';
import { readXlsx, SpreadsheetError, type SheetData } from './xlsx';
import { isZip } from './zip';

export { SpreadsheetError, type SheetData };

export const ACCEPTED_FILE_TYPES = '.csv,.tsv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Upload size cap. A 2,000-row export is well under 1 MB. */
export const MAX_FILE_BYTES = 15 * 1024 * 1024;

export const RESAVE_HINT = 'Open it in Excel or Google Sheets and save or download it as .xlsx or .csv, then upload that.';

/** Anything with a name and its bytes; a browser File, or a test double. */
export interface UploadedFile {
  name: string;
  size: number;
}

function extension(name: string): string {
  const i = name.lastIndexOf('.');
  return i < 0 ? '' : name.slice(i + 1).toLowerCase();
}

function isOleFile(bytes: Uint8Array): boolean {
  // Compound File Binary: the container of .xls and old .doc files.
  return bytes.length >= 4 && bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0;
}

/** Decode text, falling back to Windows-1252 when it isn't valid UTF-8 (old Excel CSVs). */
export function decodeText(bytes: Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/** Read the bytes of a File, with a FileReader fallback for older runtimes. */
export async function fileBytes(file: Blob): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') return new Uint8Array(await file.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsArrayBuffer(file);
  });
}

/** Parse already-read bytes. Pure apart from DOMParser / DecompressionStream for .xlsx. */
export async function parseSpreadsheet(name: string, bytes: Uint8Array): Promise<SheetData[]> {
  const ext = extension(name);
  if (isOleFile(bytes) || ext === 'xls') {
    throw new SpreadsheetError(`Old Excel .xls files aren't supported. ${RESAVE_HINT}`);
  }
  if (ext === 'numbers' || ext === 'ods') {
    throw new SpreadsheetError(`.${ext} files aren't supported. ${RESAVE_HINT}`);
  }
  if (isZip(bytes)) {
    if (ext !== 'xlsx' && ext !== 'xlsm' && ext !== '') {
      throw new SpreadsheetError(`This doesn't look like a spreadsheet. ${RESAVE_HINT}`);
    }
    return readXlsx(bytes);
  }
  if (ext === 'xlsx') throw new SpreadsheetError(`This .xlsx file is damaged. ${RESAVE_HINT}`);
  const text = decodeText(bytes);
  if (text.includes('\u0000')) throw new SpreadsheetError(`This doesn't look like a spreadsheet. ${RESAVE_HINT}`);
  const base = name.replace(/\.[^.]+$/, '') || 'Sheet 1';
  return [{ name: base, rows: parseCsv(text, ext === 'tsv' ? '\t' : undefined) }];
}

/** Read and parse an uploaded file. */
export async function readSpreadsheet(file: Blob & UploadedFile): Promise<SheetData[]> {
  if (file.size > MAX_FILE_BYTES) {
    throw new SpreadsheetError('That file is over 15 MB. Split it into smaller files.');
  }
  return parseSpreadsheet(file.name, await fileBytes(file));
}
